<template>
    <div class="card">
        <div class="bg-blue-light p-1">
            <TitleButton
                title="Payroll Statement"
                :showAddNew="false"
                :showBtn="true"
                @onClickCloseButton="navigateToListPage"
            />
        </div>

      <div class="my-2 px-2">
        <div class="row">
          <div class="col-12 col-sm-6 col-md-4 col-lg-2 mt-1">
            <v-select
                placeholder="Select Branch"
                v-model="branch"
                :options="branchList"
                label="name"
            />
           </div>
          <div class="col-12 col-sm-6 col-md-4 col-lg-2 mt-1">
            <v-select
                placeholder="Select Division"
                v-model="division"
                :options="divisionList"
                label="name"
            />
          </div>
          <div class="col-12 col-sm-6 col-md-4 col-lg-2 mt-1">
            <v-select
                placeholder="Select Department"
                v-model="department"
                :options="departmentList"
                label="name"
            />
          </div>
          <div class="col-12 col-sm-6 col-md-4 col-lg-2 mt-1">
            <v-select
                placeholder="Select Business Unit"
                v-model="businessUnit"
                :options="businessList"
                label="name"
            />
          </div>
          <div class="col-2 d-flex align-items-end">
            <button
                style="min-width: 64px;"
                @click="getPayrollData"
                class="btn btn-primary btn-block waves-effect waves-float waves-light"
            >Go
            </button>
          </div>
        </div>
      </div>

      <div class="col-12">
        <div class="d-flex justify-content-end px-2">
          <button
              @click="onClickToPrint"
              class="btn btn-outline-secondary btn-sm btn-download-invoice waves-effect me-1"
          >
            Print
          </button>
          <button
              @click="exportTable"
              class="btn btn-outline-secondary btn-sm btn-download-invoice waves-effect me-1"
          >
            Export
          </button>
        </div>
      </div>

        <div class="col-12 px-2 mt-2 mb-4">
          <div class="card" style="min-height: 200px">
            <div class="table-responsive">
              <table class="table">
                <thead>
                <tr>
                  <th>Employee ID</th>
                  <th>Employee Name</th>
                  <th v-for="(item, i) in grossComponentPayHead" :key="i">{{ item.name }}</th>
                  <th>Gross Salary</th>
                  <th v-for="(item, i) in otherEarningPayHead" :key="i">{{ item.name }}</th>
                  <th>Total Earning</th>
                  <th v-for="(item, i) in deductionPayHeadPayHead" :key="i">{{ item.name }}</th>
                  <th>Total Deduction</th>
                  <th>Net</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(item, i) in payrollData" :key="i">
                  <td>{{ item.serial_no }}</td>
                  <td>{{ item.name }}</td>
                  <td class="text-right" v-for="gh in grossComponentPayHead" :key="gh.id">
                    {{ commaFormat(item.gross_earnings[gh.id] || 0) }}
                  </td>
                  <td class="text-right">{{ commaFormat(item.gross || 0) }}</td>
                  <td class="text-right" v-for="oh in otherEarningPayHead" :key="oh.id">
                    {{ commaFormat(item.other_earnings[oh.id] || 0) }}
                  </td>
                  <td class="text-right">{{ commaFormat(item.total_earning || 0) }}</td>
                  <td class="text-right" v-for="dh in deductionPayHeadPayHead" :key="dh.id">
                    {{ commaFormat(item.deductions[dh.id] || 0) }}
                  </td>
                  <td class="text-right">{{ commaFormat(item.total_deduction || 0) }}</td>
                  <td class="text-right">{{ commaFormat(item.total) }}</td>
                </tr>
                <tr>
                  <td colspan="2">Total</td>
                  <td class="text-right" v-for="gh in grossComponentPayHead" :key="gh.id">
                    {{ commaFormat(summaryData.gross_earnings[gh.id] || 0) }}
                  </td>
                  <td class="text-right">{{ commaFormat(summaryData.total_gross || 0) }}</td>
                  <td class="text-right" v-for="oh in otherEarningPayHead" :key="oh.id">
                    {{ commaFormat(summaryData.other_earnings[oh.id] || 0) }}
                  </td>
                  <td class="text-right">{{ commaFormat(summaryData.total_earning || 0) }}</td>
                  <td class="text-right" v-for="dh in deductionPayHeadPayHead" :key="dh.id">
                    {{ commaFormat(summaryData.deductions[dh.id] || 0) }}
                  </td>
                  <td class="text-right">{{ commaFormat(summaryData.total_deduction || 0) }}</td>
                  <td class="text-right">{{ commaFormat(summaryData.total_net_payable || 0) }}</td>
                </tr>
                </tbody>
              </table>
              <p v-if="payrollData && payrollData.length < 0" class="text-center mt-5">No data</p>
            </div>
          </div>
        </div>

        <Loader v-if="loading"/>

    </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton';
import {inject, onMounted, reactive, ref} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import Loader from '@/components/atom/LoaderComponent'
import handleHrPayroll from "@/services/modules/hrPayroll/hrPayroll";
import { computed } from 'vue';
import figureFormatter from "@/services/utils/figureFormatter";
import payrollStatementPdf from '@/services/utils/pdf/payrollStatementPdf';
import handleCompany from "@/services/modules/company";
import handleDropdowns from "@/services/modules/dropdowns";

const store = useStore();
const route = useRoute();
const router = useRouter();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const loading = ref(false)
const branch = ref('')
const division = ref('')
const department = ref('')
const businessUnit = ref('')
let saveButtonLoader = ref(false)
const grossComponentPayHead = ref([]);
const otherEarningPayHead = ref([]);
const payrollStatementData = ref([]);
const deductionPayHeadPayHead = ref([]);
const payrollData = ref([]);
const summaryData = ref([]);
let departmentList = ref([])
let branchList = ref([])
let businessList = ref([])
let divisionList = ref([])
const params = ref({
    offset: 20,
});
const { commaFormat } = figureFormatter();
const token = computed(() => localStorage.getItem('token'));
const { fetchDropdownList } = handleDropdowns()
const {
  showPayrollStatementData
} = handleHrPayroll()
const { exportToPDF } = payrollStatementPdf();
const { fetchCompanyInfo } = handleCompany();
let companyInfo = reactive({})

const getPayrollData = async () => {
  let query = payrollQuery();
  let payrollStatementId = route.params.payrollStatementId;
  payrollData.value = []
  otherEarningPayHead.value = []
  grossComponentPayHead.value = []
  deductionPayHeadPayHead.value = []
  summaryData.value = []

  await showPayrollStatementData(payrollStatementId, query).then(res => {
    if (!res.status){
      return
    }
    grossComponentPayHead.value = res.data.components.gross
    otherEarningPayHead.value = res.data.components.earning
    deductionPayHeadPayHead.value = res.data.components.deduction
    summaryData.value = res.data.summary;
    payrollData.value = res.data.data;

  }).catch(err => {
    console.log(err)
  }).finally(() => {
    loading.value = false
  })
}

async function onClickToPrint() {
  let query = payrollQuery();
  payrollData.value = []
  otherEarningPayHead.value = []
  grossComponentPayHead.value = []
  deductionPayHeadPayHead.value = []
  summaryData.value = []
  payrollStatementData.value = []
  let payrollStatementId = route.params.payrollStatementId;

  await showPayrollStatementData(payrollStatementId, query).then((res) => {
    if (!res.status) {
      return
    }
    grossComponentPayHead.value = res.data.components.gross;
    otherEarningPayHead.value = res.data.components.earning;
    deductionPayHeadPayHead.value = res.data.components.deduction;
    summaryData.value = res.data.summary;
    payrollData.value = res.data.data;
    payrollStatementData.value = res.data.payroll_statement;
  }).then(() => {
    exportToPDF(
        companyInfo, grossComponentPayHead.value, otherEarningPayHead.value,
        deductionPayHeadPayHead.value, payrollData.value, summaryData.value, payrollStatementData.value
    )
  }).catch((err) => {
    console.log("Something went wrong");
  });
}

const exportTable = () => {
  let query = payrollQuery();
  let payrollStatementId = route.params.payrollStatementId;
  let url = `${process.env.VUE_APP_BASE_URL}/api/hr-payroll/payroll-statement/${payrollStatementId}${query}&_token=${token.value}&is_export=1`;
  let a = document.createElement("a");
  a.setAttribute("href", url);
  a.click();
};

const setCompanyInfo = async () => {
  await fetchCompanyInfo(route.params.companyId).then((res) => {
    companyInfo = res.data;
  }).catch((error) => {
    console.log(error);
  });
}

function navigateToListPage() {
  router.push({name: `payroll-process`, params: route.params, query: route.query});
}

function getQuery() {
  return `?company_id=${route.params.companyId}`;
}

const getDropdownList = async () => {
  let query = getQuery();
  query += '&is_branch=1&is_division=1&is_department=1&is_business=1'

  branchList.value = []
  divisionList.value = []
  departmentList.value = []
  businessList.value = []

  await fetchDropdownList(query).then(res => {
    if (!res.status){
      return
    }

    let data = res.data;
    branchList.value = data.branch_list;
    divisionList.value = data.division_list;
    departmentList.value = data.department_list;
    businessList.value = data.business_list;
  }).catch(err => {
    console.log(err)
  })
}

function payrollQuery() {
  let query = `?company_id=${route.params.companyId}`;
  if (route.query.page) query += '&page=' + route.query.page;
  if (branch.value) query += '&branch_id=' + branch.value.id;
  if (division.value) query += '&division_id=' + division.value.id;
  if (department.value) query += '&department_id=' + department.value.id;
  if (businessUnit.value) query += '&business_unit_id=' + businessUnit.value.id;
  query += '&offset=' + params.value.offset
  return query;
}

onMounted(() => {
    Promise.all([
      getPayrollData(),
      setCompanyInfo(),
      getDropdownList()
    ])
})
</script>
